import { registry, routes } from "@generated/registry";
import type { ParamValue as GeneratedParamValue, Registry } from "@generated/registry/schema";
import { createTuyau } from "@tuyau/core/client";

const client = createTuyau({
  baseUrl: import.meta.env.VITEAPPURL || "http://localhost:3333",
  registry,
});

type ParamValue = GeneratedParamValue | null;
type NormalizedParamValue = string | number | boolean | null;
type RouteParams = Record<string, ParamValue> | ParamValue[] | ParamValue;
type NormalizedRouteParams =
  | Record<string, NormalizedParamValue>
  | NormalizedParamValue[]
  | NormalizedParamValue;
type RouteName = keyof Registry & string;
type RouteQuery<Name extends RouteName> = Registry[Name]["types"]["query"];
type PublicRouteParams<Name extends RouteName> = Omit<
  Registry[Name]["types"]["params"],
  "company_slug"
>;
type RequiredKeys<T extends object> = {
  [Key in keyof T]-?: Record<never, never> extends Pick<T, Key> ? never : Key;
}[keyof T];
type RoutesWithRequiredPublicParams = {
  [Name in RouteName]: RequiredKeys<PublicRouteParams<Name>> extends never ? never : Name;
}[RouteName];
type RoutesWithoutRequiredPublicParams = Exclude<RouteName, RoutesWithRequiredPublicParams>;

type WorkspaceUrlFor = {
  <Name extends RoutesWithRequiredPublicParams>(
    identifier: Name,
    params: PublicRouteParams<Name>,
    query?: RouteQuery<Name>,
  ): string;
  <Name extends RoutesWithoutRequiredPublicParams>(
    identifier: Name,
    paramsOrQuery?: PublicRouteParams<Name> | RouteQuery<Name>,
    query?: RouteQuery<Name>,
  ): string;
};

const normalizeParamValue = (value: ParamValue): NormalizedParamValue => {
  return typeof value === "bigint" ? value.toString() : value;
};

const normalizeRouteParams = (
  params: RouteParams | undefined,
): NormalizedRouteParams | undefined => {
  if (params === undefined) {
    return undefined;
  }

  if (Array.isArray(params)) {
    return params.map(normalizeParamValue);
  }

  if (typeof params === "object" && params !== null) {
    return Object.fromEntries(
      Object.entries(params).map(([key, value]) => [key, normalizeParamValue(value)]),
    );
  }

  return normalizeParamValue(params);
};

const normalizeQuery = (query: object | undefined) => {
  if (query === undefined) {
    return undefined;
  }

  return Object.fromEntries(
    Object.entries(query).map(([key, value]) => [key, normalizeParamValue(value as ParamValue)]),
  );
};

const valueFromRecord = (value: unknown, key: string): unknown => {
  return typeof value === "object" && value !== null
    ? (value as Record<string, unknown>)[key]
    : null;
};

const companySlugFromProps = (props: unknown): string | null => {
  const auth = valueFromRecord(props, "auth");
  const authCompany = valueFromRecord(auth, "company");
  const company = valueFromRecord(props, "company") ?? authCompany;
  const subdomain = valueFromRecord(company, "subdomain");

  return typeof subdomain === "string" && subdomain.length > 0 ? subdomain : null;
};

const companySlugFromInertiaPageElement = (): string | null => {
  const page = document.querySelector<HTMLElement>("[data-page]")?.dataset.page;
  if (!page) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(page);
    return companySlugFromProps(valueFromRecord(parsed, "props"));
  } catch {
    // @expected-error-boundary Malformed or unavailable Inertia page payload; fall back to no inferred workspace slug.
    return null;
  }
};

const companySlugFromHistoryState = (): string | null => {
  return companySlugFromProps(
    valueFromRecord(valueFromRecord(window.history.state, "page"), "props"),
  );
};

const currentWorkspaceSlug = () => {
  if (typeof window === "undefined") {
    return null;
  }

  // Workspace pages live under `/w/:company_slug`; the public Help Center lives
  // under `/h/:company_slug`. Both carry the tenant slug in the path, so
  // `urlFor` can auto-inject `company_slug` for their routes.
  const match = window.location.pathname.match(/^\/[wh]\/([^/]+)/);
  return match?.[1] ?? companySlugFromHistoryState() ?? companySlugFromInertiaPageElement();
};

const publicRouteParamNames = (pattern: string) => {
  return [...pattern.matchAll(/:([A-Za-z0-9_]+)\??/g)]
    .map((match) => match[1])
    .filter((name) => name !== "company_slug");
};

const isObjectParams = (value: RouteParams | undefined): value is Record<string, ParamValue> => {
  return typeof value === "object" && value !== null && !Array.isArray(value);
};

const workspaceArgs = (identifier: string, params: RouteParams | undefined) => {
  const route = routes[identifier as keyof typeof routes];
  if (!route?.pattern.includes(":company_slug")) {
    return { params, queryFromSecondArg: undefined as object | undefined };
  }

  const companySlug = currentWorkspaceSlug();
  if (!companySlug) {
    return { params, queryFromSecondArg: undefined as object | undefined };
  }

  const publicParams = publicRouteParamNames(route.pattern);
  const hasPublicRouteParam =
    isObjectParams(params) && publicParams.some((paramName) => paramName in params);

  if (params === undefined) {
    return { params: { company_slug: companySlug }, queryFromSecondArg: undefined };
  }

  if (isObjectParams(params)) {
    if (publicParams.length === 0 || !hasPublicRouteParam) {
      return { params: { company_slug: companySlug }, queryFromSecondArg: params };
    }

    return {
      params: "company_slug" in params ? params : { company_slug: companySlug, ...params },
      queryFromSecondArg: undefined,
    };
  }

  return { params, queryFromSecondArg: undefined };
};

const buildWorkspaceUrl = (identifier: RouteName, params?: RouteParams, options?: object) => {
  const { params: routeParams, queryFromSecondArg } = workspaceArgs(identifier, params);
  const query = queryFromSecondArg ?? options;

  return client.urlFor(
    ...([
      identifier,
      normalizeRouteParams(routeParams),
      query ? { qs: normalizeQuery(query) } : undefined,
    ] as Parameters<typeof client.urlFor>),
  );
};

export const workspaceUrlFor: WorkspaceUrlFor = buildWorkspaceUrl;

export const urlFor = workspaceUrlFor;
